import { AppSettings } from "@/AppSettings";
import { IHasAudit } from "@/api/dtos";
import Field from "@/components/forms/fields/Field";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Select from "@/components/forms/inputs/Select";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { alpha, Box, Button, Checkbox, Divider, IconButton, List, ListItem, ListItemText, Paper, styled, Tooltip, useTheme } from "@mui/material";
import { useFormActions, useScopedField } from "@ngt/forms-core";
import { FC, Fragment, memo, useEffect, useMemo, useState } from "react";
import { IConfigurationSourceDataBulkEditConfiguration, IConfigurationSourceDataBulkEditData, IConfigurationSourceDataBulkEditMapping, IConfigurationSourceDataBulkEditSettings } from "./ConfigurationSourceDataBulkEdit";
import { useSnackbar } from "notistack";

export const IndentBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8),
    borderLeft: `1px solid ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)}`
}));

export const IndentDivider = styled(Divider)(({ theme }) => ({
    borderColor: alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)
}));

export interface IConfigurationSourceDataBulkEditDataType {
    type: any,
    order: number
}

export interface IConfigurationSourceDataBulkEditMultipleTypesProps<TConfigurationModel extends IConfigurationSourceDataBulkEditConfiguration, TData extends IConfigurationSourceDataBulkEditDataType> {
    data: TData[];
    settings: Record<any, IConfigurationSourceDataBulkEditSettings<TConfigurationModel, IConfigurationSourceDataBulkEditData>>;
    getItem: Record<any, (item: TData) => IConfigurationSourceDataBulkEditData>;
    useIndent: Record<any, (item: TData) => { indent: boolean, last: boolean }>
    onLinkUpdate?: () => void;
    onCancel?: () => void;
}

const ConfigurationSourceDataBulkEdit = <TConfigurationModel extends IConfigurationSourceDataBulkEditConfiguration, TData extends IConfigurationSourceDataBulkEditDataType>({
    data,
    settings,
    getItem,
    useIndent,
    onLinkUpdate,
    onCancel
}: IConfigurationSourceDataBulkEditMultipleTypesProps<TConfigurationModel, TData>) => {
    const theme = useTheme();

    const { enqueueSnackbar } = useSnackbar();

    const properties = useMemo(() => {
        return Object.keys(settings).reduce<Array<{id: string, name: string, description?: string}>>((state, current) => {
            const options = Object.keys(settings[current].mapping)
                .filter(x => (settings[current].mapping as any)[x as any] !== undefined)
                .map(x => {
                    const value = (settings[current].mapping as any)[x as any];

                    return {
                        id: x,
                        name: value.name,
                        description: value.description
                    }
                })

            for (let option of options) {
                if (!state.some(x => x.id === option.id)) {
                    state.push(option);
                }
            }

            return state;
        }, [])
    }, [settings])

    useEffect(() => {
        setFieldValue('BULK_EDIT.property', properties.find(_ => true)?.id, true, false, false, true);
    }, [properties])
    
    const { setFieldValue, getFieldValue } = useFormActions();

    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const { state: { value: selectedProperty } } = useScopedField<keyof TConfigurationModel>('BULK_EDIT.property', { value: true });

    const selectedMap = useMemo(() => {
        const map: Record<string, boolean> = {}

        for (let key of selectedItems) {
            map[key] = true;
        }

        return map;
    }, [selectedItems])

    const allSelected = selectedItems.length === data.length;
    const someSelected = selectedItems.length < data.length && selectedItems.length !== 0;

    const BulkFieldMapping = useMemo(() => {
        if (selectedItems.length === 0) {
            return undefined;
        }

        for (let index = 0; index < selectedItems.length; index++) {
            const itemKey = selectedItems[index];
    
            const itemType = data.find(x => {
                const item = getItem[x.type](x);
    
                return settings[x.type].getKey(item) === itemKey;
            })
    
            if (!itemType) {
                return undefined;
            }

            if (settings[itemType.type].mapping[selectedProperty]?.bulkEditComponent) {
                return (settings[itemType.type].mapping as any)[selectedProperty] as IConfigurationSourceDataBulkEditMapping<IConfigurationSourceDataBulkEditData>;
            }
        }
    }, [selectedItems, settings, getItem, data, selectedProperty]);

    const BulkFieldComponent = BulkFieldMapping?.bulkEditComponent;

    return (
        <>
            <Box
                sx={{
                    my: 2,
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center'
                }}
            >
                
                <Box
                    sx={{
                        ml: 2
                    }}
                >
                    <Tooltip 
                        title={
                            allSelected ?
                                'Unselect All' :
                                'Select All'
                        }
                    >
                        <Checkbox 
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(_, checked) => {
                                if (selectedItems.length === 0) {
                                    setFieldValue('BULK_EDIT.value', undefined, true, false, false, true);
                                }

                                if (selectedItems.length < data.length) {
                                    setSelectedItems(data.map(x => {
                                        const item = getItem[x.type](x);
                                        
                                        return settings[x.type].getKey(item);
                                    }));
                                }
                                else {
                                    setSelectedItems([])
                                }
                            }}
                        />
                    </Tooltip>
                </Box>
                <InputOnlyField
                    propertyPath="BULK_EDIT.property"
                    label="Property"
                    FormControlProps={{
                        margin: 'none'
                    }}
                    nullOption={false}
                    component={Select}
                    options={properties}
                />
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        width: 120,
                        minWidth: 120,
                        maxWidth: 120,
                        minHeight: 52
                    }}
                    onClick={onCancel}
                >
                    Bulk Edit
                </Button>
            </Box> 
            <Paper>
                <List disablePadding>
                    {
                        data.map((itemType, index, array) => {
                            const {
                                mapping,
                                getLocalConfigurationPropertyPath,
                                getParentConfigurationPropertyPath,
                                getKey,
                                copyConfiguration,
                                createNewConfiguration
                            } = settings[itemType.type];

                            const item = getItem[itemType.type](itemType);

                            const useItemIndent = useIndent[itemType.type](itemType); 

                            const FieldComponent = selectedProperty ? mapping[selectedProperty]?.component : undefined;
                            const canBulkEdit = selectedProperty ? !!mapping[selectedProperty]?.bulkEditComponent : false;

                            const localConfigurationPropertyPath = getLocalConfigurationPropertyPath(item);
                            const parentConfigurationPropertyPath = getParentConfigurationPropertyPath(item);
                            const key = getKey(item);

                            const WrapperComponent = useItemIndent.indent ? IndentBox : Fragment;

                            return (
                                <Fragment
                                    key={key}
                                >
                                    <WrapperComponent>
                                        <Field
                                            propertyPath={item.linked ? 
                                                parentConfigurationPropertyPath :
                                                localConfigurationPropertyPath
                                            }
                                        >
                                            <ListItem
                                                sx={{
                                                    gap: 4,
                                                    py: 2
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        maxWidth: useItemIndent.indent ? `calc(50% - ${theme.spacing(4)})` : '50%',
                                                        minWidth: useItemIndent.indent ? `calc(50% - ${theme.spacing(4)})` : '50%',
                                                        gap: 2
                                                    }}
                                                >
                                                    <Box>
                                                        <Checkbox 
                                                            checked={!!selectedMap[key]}
                                                            onChange={(_, checked) => {
                                                                if (selectedItems.length === 0) {
                                                                    setFieldValue('BULK_EDIT.value', undefined, true, false, false, true);
                                                                }

                                                                if (checked) {
                                                                    setSelectedItems(current => {
                                                                        return [...current, key]
                                                                    })
                                                                }
                                                                else {
                                                                    setSelectedItems(current => {
                                                                        return current.filter(y => y !== key)
                                                                    })
                                                                }
                                                            }}
                                                            disabled={!canBulkEdit}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <FontAwesomeIcon fixedWidth size="lg" icon={item.icon} />
                                                    </Box>
                                                    <ListItemText 
                                                        primary={item.displayName} 
                                                    />
                                                    {
                                                        FieldComponent ?
                                                            item.linked ?
                                                                <IconButton
                                                                    onClick={() => {
                                                                        setFieldValue<TConfigurationModel>(localConfigurationPropertyPath, currentValue => {
                                                                            currentValue.linked = false;
                                                                
                                                                            const linkedValue = getFieldValue<TConfigurationModel>(parentConfigurationPropertyPath);
                                                                
                                                                            if (linkedValue) {
                                                                                return copyConfiguration(linkedValue, currentValue);
                                                                            }
                                                                
                                                                            return currentValue;
                                                                        }, true, true, true);

                                                                        onLinkUpdate?.();
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={AppSettings.icons.link} />
                                                                </IconButton> :
                                                                <IconButton
                                                                    onClick={() => {
                                                                        setFieldValue<TConfigurationModel>(localConfigurationPropertyPath, currentValue => {
                                                                            currentValue.linked = true;
                                                                            return copyConfiguration(createNewConfiguration(item), currentValue)
                                                                        }, true, true, true);

                                                                        onLinkUpdate?.();
                                                                    }} 
                                                                >
                                                                    <FontAwesomeIcon icon={AppSettings.icons.unlink} />
                                                                </IconButton> :
                                                            null
                                                    }
                                                </Box>
                                                <Box
                                                    sx={{
                                                        flex: '1 1 auto'
                                                    }}
                                                >
                                                    {
                                                        FieldComponent && (
                                                            <FieldComponent {...item} />
                                                        )
                                                    }
                                                </Box>
                                            </ListItem>
                                            {!useItemIndent.last && (index + 1) < array.length && <Divider />}
                                        </Field>
                                    </WrapperComponent>
                                    {useItemIndent.indent && useItemIndent.last && <IndentDivider />}
                                </Fragment>
                                
                            )
                        })
                    }
                </List>
            </Paper>
            {
                selectedItems.length > 0 && BulkFieldComponent && (
                    <Paper
                        
                        sx={{
                            borderRadius: 0,
                            position: 'sticky',
                            bottom: 0,
                            mx: -3,
                            mt: 3,
                            mb: -3,
                            padding: 3,
                            zIndex: 1,
                            display: 'flex',
                            gap: 4,
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            sx={{ 
                                minWdith: 500,
                                width: 500,
                                maxWidth: 500
                            }}
                        >
                            Update {selectedItems.length} Item(s)
                        </Box>
                        <Field
                            propertyPath="BULK_EDIT"
                        >
                            <BulkFieldComponent />
                        </Field>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                const value = getFieldValue('BULK_EDIT.value');

                                if (BulkFieldMapping.required === true && (value === null || value === undefined)) {
                                    enqueueSnackbar(`This field must have a value!`, { variant: "error" });
                                    return;
                                } 

                                for (let selectedKey of selectedItems) {
                                    const itemType = data.find(x => {
                                        const item = getItem[x.type](x);
                            
                                        return settings[x.type].getKey(item) === selectedKey;
                                    })

                                    if (!itemType) {
                                        continue;
                                    }

                                    const item = getItem[itemType.type](itemType);

                                    if (!item) {
                                        continue;
                                    }



                                    const {
                                        getLocalConfigurationPropertyPath,
                                        getParentConfigurationPropertyPath,
                                        copyConfiguration,
                                        mapping
                                    } = settings[itemType.type];

                                    const canBulkEdit = selectedProperty ? !!mapping[selectedProperty]?.bulkEditComponent : false;

                                    if (!canBulkEdit) {
                                        continue;
                                    }

                                    const localConfigurationPropertyPath = getLocalConfigurationPropertyPath(item);

                                    if (item?.linked) {
                                        const parentConfigurationPropertyPath = getParentConfigurationPropertyPath(item);

                                        setFieldValue<TConfigurationModel>(localConfigurationPropertyPath, currentValue => {
                                            currentValue.linked = false;
                                
                                            const linkedValue = getFieldValue<TConfigurationModel>(parentConfigurationPropertyPath);
                                
                                            if (linkedValue) {
                                                return copyConfiguration(linkedValue, currentValue);
                                            }
                                
                                            return currentValue;
                                        }, true, true, true);

                                        onLinkUpdate?.();
                                    }

                                    setFieldValue(`${localConfigurationPropertyPath}.${selectedProperty as string}`, value);
                                }
                            }}
                        >
                            Update
                        </Button>
                    </Paper>
                )
            }
        </>
    )
}

export default memo(ConfigurationSourceDataBulkEdit) as typeof ConfigurationSourceDataBulkEdit;